/**
 * LocalStorage key for idle timer expiration
 * @constant {string}
 */
const STORAGE_KEY = '_beebeeScreenSaver';

/**
 * Interval for checking expiration (in milliseconds)
 * @constant {number}
 */
const CHECK_INTERVAL_MS = 1000;

/**
 * Debounce delay for updating expiration time (in milliseconds)
 * @constant {number}
 */
const UPDATE_DEBOUNCE_MS = 300;

/**
 * Events to track for user activity
 * @constant {string[]}
 */
const ACTIVITY_EVENTS = Object.freeze(['mousemove', 'scroll', 'keydown', 'touchstart']);

/**
 * IdleTimer Class
 *
 * Monitors user activity and triggers callbacks when the user has been idle
 * for a specified timeout period. Uses localStorage for cross-tab synchronization.
 *
 * @class IdleTimer
 *
 * @example
 * const timer = new IdleTimer({
 *   timeout: 300000, // 5 minutes
 *   onTimeout: () => console.log('User is idle'),
 *   onExpired: () => console.log('Session expired')
 * });
 *
 * // Later, to stop the timer:
 * timer.cleanUp();
 */
class IdleTimer {
  /**
   * Creates an IdleTimer instance
   * @param {Object} options - Configuration options
   * @param {number} options.timeout - Idle timeout in milliseconds
   * @param {Function} options.onTimeout - Callback when user becomes idle
   * @param {Function} [options.onExpired] - Callback if session was already expired on init
   */
  constructor({
    timeout,
    onTimeout,
    onExpired
  }) {
    // Initialize state
    this.timeout = timeout;
    this.onTimeout = onTimeout;
    this.interval = null;
    this.timeoutTracker = null;
    this.eventHandler = null;
    this._isInitialized = false;

    // Only initialize on HTTPS with localStorage support
    if (!this._isEnvironmentSupported()) {
      return;
    }

    // Check for existing expired session
    const expiredTime = this._getExpiredTime();
    if (expiredTime > 0 && expiredTime < Date.now()) {
      if (typeof onExpired === 'function') {
        onExpired();
      }
    }

    // Bind event handler once
    this.eventHandler = this.updateExpiredTime.bind(this);

    // Start tracking
    this._attachEventListeners();
    this.startInterval();
    this._isInitialized = true;
  }

  // ==================== Private Methods ====================

  /**
   * Checks if the environment supports the idle timer
   * @returns {boolean}
   * @private
   */
  _isEnvironmentSupported() {
    try {
      return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined' && window.location?.protocol === 'https:';
    } catch {
      return false;
    }
  }

  /**
   * Safely gets the expired time from localStorage
   * @returns {number}
   * @private
   */
  _getExpiredTime() {
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      return parseInt(value || '0', 10) || 0;
    } catch {
      return 0;
    }
  }

  /**
   * Safely sets the expired time in localStorage
   * @param {number} time - Timestamp to set
   * @private
   */
  _setExpiredTime(time) {
    try {
      localStorage.setItem(STORAGE_KEY, String(time));
    } catch {
      // Silently fail if localStorage is not available
    }
  }

  /**
   * Safely removes the expired time from localStorage
   * @private
   */
  _removeExpiredTime() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Silently fail if localStorage is not available
    }
  }

  /**
   * Attaches event listeners for activity tracking
   * @private
   */
  _attachEventListeners() {
    if (!this.eventHandler) return;
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, this.eventHandler, {
        passive: true
      });
    }
  }

  /**
   * Detaches event listeners for activity tracking
   * @private
   */
  _detachEventListeners() {
    if (!this.eventHandler) return;
    for (const event of ACTIVITY_EVENTS) {
      window.removeEventListener(event, this.eventHandler);
    }
  }

  // ==================== Public Methods ====================

  /**
   * Starts the interval that checks for idle timeout
   */
  startInterval() {
    // Clear any existing interval
    if (this.interval) {
      clearInterval(this.interval);
    }

    // Set initial expiration time
    this.updateExpiredTime();
    this.interval = setInterval(() => {
      const expiredTime = this._getExpiredTime();
      if (expiredTime > 0 && expiredTime < Date.now()) {
        if (typeof this.onTimeout === 'function') {
          this.onTimeout();
        }
        this.cleanUp();
      }
    }, CHECK_INTERVAL_MS);
  }

  /**
   * Updates the expiration time (debounced)
   * Called on user activity events
   */
  updateExpiredTime() {
    if (this.timeoutTracker) {
      clearTimeout(this.timeoutTracker);
    }
    this.timeoutTracker = setTimeout(() => {
      this._setExpiredTime(Date.now() + this.timeout);
    }, UPDATE_DEBOUNCE_MS);
  }

  /**
   * Attaches activity event listeners
   * @deprecated Use constructor initialization instead
   */
  tracker() {
    this._attachEventListeners();
  }

  /**
   * Cleans up all timers and event listeners
   * Should be called when the timer is no longer needed
   */
  cleanUp() {
    // Clear debounce timeout
    if (this.timeoutTracker) {
      clearTimeout(this.timeoutTracker);
      this.timeoutTracker = null;
    }

    // Clear check interval
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }

    // Remove event listeners
    this._detachEventListeners();

    // Clear localStorage
    this._removeExpiredTime();
    this._isInitialized = false;
  }

  /**
   * Checks if the timer is currently active
   * @returns {boolean}
   */
  get isActive() {
    return this._isInitialized && this.interval !== null;
  }

  /**
   * Gets the remaining time before idle timeout (in milliseconds)
   * @returns {number} Remaining time, or 0 if expired/not initialized
   */
  get remainingTime() {
    if (!this._isInitialized) {
      return 0;
    }
    const expiredTime = this._getExpiredTime();
    const remaining = expiredTime - Date.now();
    return remaining > 0 ? remaining : 0;
  }
}

export { IdleTimer as default };
//# sourceMappingURL=idle_timer.js.map
